import { computed, ref, watch, onScopeDispose, type ComputedRef, type Ref } from 'vue'

// Full-text search against `/api/v1/search`. Owns the debounced watch
// on `query`, the result/error/loading state, and a manual `runSearch`
// entry point that `Retry` buttons can hit directly. Moved out of
// `WikiSidebar.vue` so the catalog template stays focused on menu
// rendering and the search edge cases (empty state, error, retry) are
// testable in isolation.
//
// Optional `activeFilter`: when the caller passes a ref
// holding the active category-chip key, search requests carry it as
// `&category=` so the query searches WITHIN the filtered set rather than
// wiki-wide. Changing the filter while a query is live re-runs the
// search so the results re-scope immediately. A null/blank ref value (or
// no ref at all — the sidebar's jump-to search) means wiki-wide search.

const SEARCH_MIN_CHARS = 2
const SEARCH_DEBOUNCE_MS = 180

export interface SidebarSearchResult {
  path: string
  title: string
  snippet: string
}

export interface SidebarSearch {
  query: Ref<string>
  results: Ref<SidebarSearchResult[]>
  searching: Ref<boolean>
  error: Ref<string | null>
  isSearchMode: ComputedRef<boolean>
  runSearch: (q: string) => Promise<void>
  clear: () => void
}

export function useSidebarSearch(activeFilter?: Ref<string | null>): SidebarSearch {
  const query = ref('')
  const results = ref<SidebarSearchResult[]>([])
  const searching = ref(false)
  const error = ref<string | null>(null)
  const isSearchMode = computed(() => query.value.trim().length >= SEARCH_MIN_CHARS)

  let timer: number | undefined

  async function runSearch(q: string): Promise<void> {
    searching.value = true
    error.value = null
    try {
      let url = `/api/v1/search?q=${encodeURIComponent(q)}`
      const category = activeFilter?.value
      if (category) url += `&category=${encodeURIComponent(category)}`
      const res = await fetch(url, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (res.ok) {
        results.value = await res.json()
      } else {
        results.value = []
        error.value = `Search failed (${res.status}). Try again.`
      }
    } catch (e) {
      results.value = []
      error.value = e instanceof Error ? e.message : 'Search failed. Try again.'
    } finally {
      searching.value = false
    }
  }

  function clear(): void {
    query.value = ''
    results.value = []
    error.value = null
  }

  watch(query, (value) => {
    window.clearTimeout(timer)
    const trimmed = value.trim()
    if (trimmed.length < SEARCH_MIN_CHARS) {
      results.value = []
      error.value = null
      return
    }
    timer = window.setTimeout(() => runSearch(trimmed), SEARCH_DEBOUNCE_MS)
  })

  // Re-scope a live search when the active filter changes — so flipping
  // a category chip mid-search re-queries within the new scope rather
  // than leaving stale wiki-wide (or other-category) results on screen.
  if (activeFilter) {
    watch(activeFilter, () => {
      const trimmed = query.value.trim()
      if (trimmed.length >= SEARCH_MIN_CHARS) void runSearch(trimmed)
    })
  }

  onScopeDispose(() => window.clearTimeout(timer))

  return { query, results, searching, error, isSearchMode, runSearch, clear }
}
